import { Component, forwardRef, ElementRef, ChangeDetectionStrategy, input, output, viewChild } from '@angular/core';
import { FormControl, ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger, MatAutocomplete, MatOption } from '@angular/material/autocomplete';
import { MatChipInputEvent, MatChipGrid, MatChipRow, MatChipRemove, MatChipInput } from '@angular/material/chips';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { RxReactiveFormsModule } from '@rxweb/reactive-form-validators';
import { AsyncPipe, UpperCasePipe } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
    selector: 'app-chips-list',
    templateUrl: './chips-list.component.html',
    styleUrls: ['./chips-list.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ChipsListComponent),
            multi: true
        }
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [MatFormField, MatLabel, MatChipGrid, MatChipRow, MatChipRemove, MatIcon, MatInput, MatChipInput, FormsModule, MatAutocompleteTrigger, ReactiveFormsModule, RxReactiveFormsModule, MatAutocomplete, MatOption, AsyncPipe, UpperCasePipe, TranslatePipe]
})
export class ChipsListComponent implements ControlValueAccessor {
  readonly allOptions = input<string[]>([]);
  readonly selectionChange = output<string[]>();
  readonly label = input('');
  readonly placeholder = input('');
  readonly prefix = input('');
  readonly appearance = input<'fill' | 'outline'>('fill');

  readonly chipInput = viewChild<ElementRef>('autoCompleteChipInput');

  availableOptions: string[] = [];
  selectedOptions: string[] = [];
  inputControl = new FormControl({ value: '', disabled: false });
  filteredOptions!: Observable<string[]>;

  private onChange: (value: string[]) => void = () => {};
  private onTouched: () => void = () => {};

  constructor() {}

  ngOnInit(): void {
    this.availableOptions = [...this.allOptions()];
    this.filteredOptions = this.inputControl.valueChanges.pipe(
      startWith(''),
      map(value => this.filter(value || ''))
    );
  }

  writeValue(value: string[]): void {
    this.selectedOptions = value || [];
    this.updateAvailableOptions();
  }

  registerOnChange(fn: (value: string[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  selectOption(event: MatAutocompleteSelectedEvent): void {
    const option: string = event.option.value;
    if (!this.selectedOptions.includes(option)) {
      this.selectedOptions.push(option);
      this.updateAvailableOptions();
      this.onChange(this.selectedOptions);
      this.selectionChange.emit(this.selectedOptions);
    }
    this.resetInput();
  }

  removeOption(option: string): void {
    this.selectedOptions = this.selectedOptions.filter(o => o !== option);
    this.focusInput();
    this.onChange(this.selectedOptions);
    this.selectionChange.emit(this.selectedOptions);
  }

  focusInput(): void {
    if (!this.inputControl.value) {
      this.updateAvailableOptions();
      this.refreshFilteredOptions();
    }
  }

  private filter(value: string): string[] {
    const filterValue = value.toLowerCase();
    return this.availableOptions.filter(option =>
      option.toLowerCase().includes(filterValue)
    );
  }

  private updateAvailableOptions(): void {
    this.availableOptions = this.allOptions().filter(option => !this.selectedOptions.includes(option));
  }

  private refreshFilteredOptions(): void {
    const currentValue = this.inputControl.value || '';
    this.resetInput()
    setTimeout(() => this.inputControl.setValue(currentValue), 0);
  }

  private resetInput(): void {
    this.chipInput()!.nativeElement.value = '';
    this.inputControl.reset();
  }
}